import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PostPresenter from "./PostPresenter";
import { fetchAccounts, createPost } from "./util";
import { PostForMeAccount, PostForMeAccountStatus } from "./models";
import { PATHS } from "src/routes";
import { ContentBox } from "@arelia-components/ui-atoms/ContentBox";
import Header from "@arelia-components/layout/Header";
import { useConfiguration } from "src/api/configuration";
import { toast } from "sonner";
import AppContext from "src/AppContext";
import { Box, Button, Typography } from "@mui/material";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { SubscriptionPlanName } from "@arelia-types/subscriptions";
import { useQueryClient } from "react-query";

export default function PostContainer() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<PostForMeAccount[]>([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const { data: configuration } = useConfiguration();
  const { account } = useContext(AppContext);
  const queryClient = useQueryClient();

  const planSupportsMySocial = [SubscriptionPlanName.MySocial, SubscriptionPlanName.MySocialMyFans].includes(account?.plan as SubscriptionPlanName);

  const handleUpgrade = () => {
    navigate("/account/subscription?changePlan=true");
  };

  const enablePosting = configuration?.flags?.EnablePosting === true;

  const navOptions = [
    { value: PATHS.SOCIAL + "/dashboard", label: "Dashboard" },
    { value: PATHS.SOCIAL + "/my-videos", label: "My Videos" },
    ...(enablePosting ? [{ value: PATHS.SOCIAL + "/create-a-post", label: "Create a post" }] : [])
  ];

  const loadAccounts = useCallback(async () => {
    try {
      const list = await fetchAccounts();
      const connectedList = list.filter(a => a.status === PostForMeAccountStatus.Connected);
      setAccounts(connectedList);
      setSelectedAccountIds(prev => prev.filter(id => connectedList.some(a => a.id === id)));
    } catch (e: any) {
      toast.error(e.message ?? "Failed to load accounts");
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const submitPost = async (body: any) => {
    try {
      await createPost(body);
      toast.success("Successfully posted");
      queryClient.invalidateQueries(["usePostsConfig"]);
    } catch (e: any) {
      toast.error(e.message ?? "Failed to create post");
    }
  };

  return (
    <ContentBox>
      <Header
        title="My Social"
        navOptions={navOptions}
        navValue={pathname}
        setNavValue={navigate}
      />
      {planSupportsMySocial ? (
        <PostPresenter
          accounts={accounts}
          selectedAccountIds={selectedAccountIds}
          setSelectedAccountIds={setSelectedAccountIds}
          submitPost={submitPost}
        />
      ) : (
          <Box sx={styles.upgradeContainer}>
            <Typography variant="h5" sx={styles.upgradeText}>
              Your current subscription doesn't include My Social features
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<AutoAwesomeIcon />}
              onClick={handleUpgrade}
              sx={styles.upgradeButton}
            >
              Upgrade to My Social
            </Button>
          </Box>
        )}
    </ContentBox>
  );
}

const styles = {
  upgradeContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
    width: '100%',
    gap: '24px'
  },
  upgradeText: {
    textAlign: 'center',
    marginBottom: '16px',
    color: '#555'
  },
  upgradeButton: {
    padding: '12px 24px',
    fontSize: '16px'
  }
};
